import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";

// The product tour's state. Steps now come from the server rather than from a
// file in this bundle, so a tour can be built and edited without a deploy.
// Onboarding completion belongs to the person, not to the account they happen
// to be viewing, so nothing here is company-scoped.

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

const initialState = {
  // null until the server has told us; we never auto-open a tour on a guess.
  required: null,
  version: null,
  // The tour currently loaded for running: { id, name, tour_steps: [...] }
  tour: null,
  tourLoading: false,
  // True while an admin is running a draft from the builder. A preview is not a
  // real run, so it must not touch the person's onboarding state or the funnel.
  preview: false,
  running: false,
  stepIndex: 0,
  loading: false,
};

export const fetchOnboarding = createAsyncThunk(
  "onboarding/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/onboarding`, {
        headers: authHeaders(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to load onboarding");
    }
  }
);

// The first-sign-in tour for this person's role, or null if none is published.
export const loadOnboardingTour = createAsyncThunk(
  "onboarding/loadOnboardingTour",
  async ({ selectedAccount } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/tours/onboarding`, {
        headers: authHeaders(),
        params: selectedAccount ? { account_id: selectedAccount } : undefined,
      });
      return response.data?.tour || null;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to load tour");
    }
  }
);

// A specific tour — what a notification's "Start the tour" button loads.
export const loadTour = createAsyncThunk(
  "onboarding/loadTour",
  async ({ id, selectedAccount, preview = false }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/tours/${id}`, {
        headers: authHeaders(),
        params: {
          ...(selectedAccount ? { account_id: selectedAccount } : {}),
          // Lets an admin run a draft they are building. The server only
          // honours it for an uber admin.
          ...(preview ? { preview: true } : {}),
        },
      });
      return response.data?.tour || null;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to load tour");
    }
  }
);

// Reports are sent one at a time, in the order they were raised. Fired in
// parallel, a late "started" could land after the final "completed" and leave
// the run looking unfinished.
let progressQueue = Promise.resolve();

// Where someone got to, for the drop-off funnel. Deliberately fire-and-forget:
// a failed write costs a data point, never the walkthrough someone is in.
export const reportTourProgress = createAsyncThunk(
  "onboarding/progress",
  ({ tourId, stepIndex, status }) => {
    progressQueue = progressQueue.then(async () => {
      try {
        await axios.post(
          `${apiBaseUrl}/tours/${tourId}/progress`,
          { step_index: stepIndex, status },
          { headers: authHeaders() }
        );
      } catch {
        /* analytics must never interrupt a tour */
      }
    });
    return progressQueue.then(() => null);
  }
);

// state: "completed" | "skipped" | "reset"
export const setOnboardingState = createAsyncThunk(
  "onboarding/set",
  async ({ state, version }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${apiBaseUrl}/onboarding`,
        { onboarding: { state, version } },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to save onboarding");
    }
  }
);

export const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    startTour: (state) => {
      state.running = true;
      state.stepIndex = 0;
    },
    nextStep: (state) => {
      state.stepIndex += 1;
    },
    previousStep: (state) => {
      state.stepIndex = Math.max(0, state.stepIndex - 1);
    },
    goToStep: (state, action) => {
      state.stepIndex = Math.max(0, action.payload || 0);
    },
    // Ends the tour locally. The server write is dispatched separately so the
    // overlay closes instantly rather than waiting on a round trip.
    endTour: (state) => {
      state.running = false;
      state.stepIndex = 0;
      state.required = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOnboarding.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOnboarding.fulfilled, (state, action) => {
        state.loading = false;
        state.required = Boolean(action.payload?.required);
        state.version = action.payload?.version || null;
      })
      .addCase(fetchOnboarding.rejected, (state) => {
        state.loading = false;
        // Failing closed matters here: a network hiccup must never pop an
        // unsolicited tour over someone's work.
        state.required = false;
      })
      .addCase(loadOnboardingTour.pending, (state) => {
        state.tourLoading = true;
      })
      .addCase(loadOnboardingTour.fulfilled, (state, action) => {
        state.tourLoading = false;
        state.tour = action.payload;
        state.preview = false;
      })
      .addCase(loadOnboardingTour.rejected, (state) => {
        state.tourLoading = false;
        state.tour = null;
      })
      .addCase(loadTour.pending, (state) => {
        state.tourLoading = true;
      })
      .addCase(loadTour.fulfilled, (state, action) => {
        state.tourLoading = false;
        state.tour = action.payload;
        state.preview = Boolean(action.meta.arg?.preview);
      })
      .addCase(loadTour.rejected, (state) => {
        state.tourLoading = false;
        state.tour = null;
      })
      .addCase(setOnboardingState.fulfilled, (state, action) => {
        state.required = Boolean(action.payload?.required);
        state.version = action.payload?.version || state.version;
      });
  },
});

export const { startTour, nextStep, previousStep, goToStep, endTour } =
  onboardingSlice.actions;

export default onboardingSlice.reducer;
